import React, { useCallback, useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import i18next from 'i18next';

const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);
import {
  RotateCcw, Camera, ShieldAlert, Loader2, CheckCircle2, History, Crown, Eye,
} from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(getUiLocale()) : '—');

function ConfirmDialog({ open, onOpenChange, title, description, phrase, busy, onConfirm, children, testid }) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (!open) setTyped(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-destructive" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">{L('Type','Escribe')} <span className="font-mono font-semibold">{phrase}</span> {L('to confirm','para confirmar')}</p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={phrase} data-testid={`${testid}-phrase`} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testid}-cancel`}>{L('Cancel','Cancelar')}</Button>
          <Button variant="destructive" disabled={busy || typed !== phrase} onClick={() => onConfirm(typed)} data-testid={`${testid}-confirm`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriftBadge({ drift }) {
  if (!drift) return <Badge variant="outline" data-testid="drift-badge">{L('No canonical snapshot','Sin snapshot canónico')}</Badge>;
  if (drift.error) return <Badge variant="outline" className="text-destructive" data-testid="drift-badge">{L('Drift check failed','Falló la verificación de desviación')}</Badge>;
  if (drift.clean) {
    return (
      <Badge variant="outline" className="text-success border-success/40 gap-1" data-testid="drift-badge">
        <CheckCircle2 className="h-3 w-3" /> In sync with {drift.golden_version}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-warning border-warning/40" data-testid="drift-badge">
      {drift.modified} modified · {drift.added} added · {drift.removed} removed since {drift.golden_version}
    </Badge>
  );
}

export default function AdminDemoReset() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [markCanonical, setMarkCanonical] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/demo-reset/status');
      setStatus(data);
      setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/demo-reset/dry-run');
      setDryRun(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doReset = async (phrase) => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/demo-reset/execute', { confirmation: phrase, reason: reason.trim() || null });
      if (data.rolled_back) toast.error(`Reset rolled back: ${data.outcome}`);
      else toast.success(`Demo tenant reset to ${data.golden_version} in ${data.duration_ms} ms`);
      setResetOpen(false); setReason(''); setDryRun(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doCapture = async (phrase) => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/demo-reset/capture-golden', {
        confirmation: phrase, description: description.trim(), notes: notes.trim() || null, set_canonical: markCanonical,
      });
      toast.success(`Golden ${data.version} captured (${data.total_documents} documents)`);
      setCaptureOpen(false); setDescription(''); setNotes('');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setCanonical = async (version) => {
    try {
      await api.post('/admin/demo-reset/set-canonical', { confirmation: 'SET CANONICAL', version });
      toast.success(`${version} is now the Canonical Demo State`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (notFound || !status) {
    return (
      <>
        <Topbar title="Demo Reset" />
        <div className="p-8" data-testid="demo-reset-unavailable">
          <Card className="p-8 text-sm text-muted-foreground">{L('This feature is not available on this environment or account.','Esta función no está disponible en este entorno o cuenta.')}</Card>
        </div>
      </>
    );
  }

  const canonical = status.canonical;

  return (
    <>
      <Topbar title="Demo Reset" subtitle="Golden Snapshot → Scoped Restore · Bomberos demo tenant" />
      <div className="p-6 lg:p-8 space-y-6" data-testid="demo-reset-panel">
        {/* Status card */}
        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{L('Canonical Demo State','Estado demo canónico')}</p>
              <p className="text-lg font-semibold mt-1" data-testid="canonical-version">
                {canonical ? `${canonical.version} — ${canonical.description}` : 'Not captured yet'}
              </p>
              {canonical && <p className="text-xs text-muted-foreground">{fmt(canonical.created_at)} · {canonical.created_by_name} · {canonical.total_documents} documents</p>}
            </div>
            <DriftBadge drift={status.drift} />
          </div>
          <div className="flex gap-2 flex-wrap pt-1">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={runDryRun} disabled={busy || !canonical} data-testid="dry-run-btn">
              <Eye className="h-4 w-4" /> Preview reset (dry-run)
            </Button>
            <Button size="sm" variant="destructive" className="gap-1.5" onClick={() => setResetOpen(true)} disabled={busy || !canonical} data-testid="reset-btn">
              <RotateCcw className="h-4 w-4" /> Reset demo tenant
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => setCaptureOpen(true)} disabled={busy} data-testid="capture-btn">
              <Camera className="h-4 w-4" /> Capture new golden version
            </Button>
          </div>
        </Card>

        {/* Dry run result */}
        {dryRun && (
          <Card className="p-5" data-testid="dry-run-result">
            <p className="text-sm font-semibold mb-1">Dry-run against {dryRun.golden_version} — integrity {dryRun.integrity}</p>
            <p className="text-xs text-muted-foreground mb-3">Will delete {dryRun.total_delete} · will restore {dryRun.total_restore} documents. Nothing has been written.</p>
            <Table>
              <TableHeader><TableRow><TableHead>{L('Collection','Colección')}</TableHead><TableHead className="text-right">{L('Will delete','Se eliminarán')}</TableHead><TableHead className="text-right">{L('Will restore','Se restaurarán')}</TableHead></TableRow></TableHeader>
              <TableBody>
                {dryRun.per_collection.map((c) => (
                  <TableRow key={c.collection}>
                    <TableCell className="font-mono text-xs">{c.collection}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.will_delete}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.will_restore}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Version registry */}
        <Card className="p-5" data-testid="golden-registry">
          <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Crown className="h-4 w-4" /> Golden version registry</p>
          {status.registry.length === 0 ? (
            <p className="text-sm text-muted-foreground">{L('No golden snapshots captured yet.','Aún no se han capturado snapshots golden.')}</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>{L('Version','Versión')}</TableHead><TableHead>{L('Created','Creado')}</TableHead><TableHead>{L('By','Por')}</TableHead><TableHead>{L('Description','Descripción')}</TableHead><TableHead className="text-right">Docs</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {status.registry.map((r) => (
                  <TableRow key={r.version} data-testid={`golden-row-${r.version}`}>
                    <TableCell className="font-semibold">{r.version} {r.canonical && <Badge className="ml-1">{L('Canonical','Canónico')}</Badge>}</TableCell>
                    <TableCell className="text-xs">{fmt(r.created_at)}</TableCell>
                    <TableCell className="text-xs">{r.created_by_name}</TableCell>
                    <TableCell className="text-xs">{r.description}{r.notes ? <span className="text-muted-foreground"> · {r.notes}</span> : null}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.total_documents}</TableCell>
                    <TableCell className="text-right">
                      {!r.canonical && (
                        <Button size="sm" variant="ghost" onClick={() => setCanonical(r.version)} data-testid={`set-canonical-${r.version}`}>{L('Set as canonical','Marcar como canónico')}</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Reset history */}
        <Card className="p-5" data-testid="reset-history">
          <p className="text-sm font-semibold mb-3 flex items-center gap-2"><History className="h-4 w-4" /> History</p>
          {status.history.length === 0 ? (
            <p className="text-sm text-muted-foreground">{L('No operations recorded yet.','Aún no hay operaciones registradas.')}</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>{L('Date','Fecha')}</TableHead><TableHead>{L('Operation','Operación')}</TableHead><TableHead>{L('Golden','Golden')}</TableHead><TableHead>{L('Duration','Duración')}</TableHead><TableHead>{L('By','Por')}</TableHead><TableHead>Backup</TableHead><TableHead>Rollback</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody>
                {status.history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-xs whitespace-nowrap">{fmt(h.at)}</TableCell>
                    <TableCell className="text-xs">{h.op}</TableCell>
                    <TableCell className="text-xs">{h.golden_version || '—'}</TableCell>
                    <TableCell className="text-xs tabular-nums">{h.duration_ms != null ? `${h.duration_ms} ms` : '—'}</TableCell>
                    <TableCell className="text-xs">{h.actor_name}</TableCell>
                    <TableCell className="text-xs font-mono max-w-[180px] truncate" title={h.backup_path}>{h.backup_path ? h.backup_path.split('/').pop() : '—'}</TableCell>
                    <TableCell className="text-xs">{h.rolled_back ? <Badge variant="destructive">yes</Badge> : 'no'}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" title={h.reason}>{h.reason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {/* Reset confirm */}
      <ConfirmDialog
        open={resetOpen} onOpenChange={setResetOpen} busy={busy}
        title="Reset the Bomberos demo tenant"
        description={`All tenant data will be replaced with the Canonical Demo State (${canonical?.version}). A pre-reset backup is captured automatically.`}
        phrase="RESET BOMBEROS" onConfirm={doReset} testid="reset-dialog"
      >
        {dryRun && (
          <p className="text-xs text-muted-foreground">Dry-run: will delete {dryRun.total_delete} and restore {dryRun.total_restore} documents.</p>
        )}
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">{L('Reason (optional)','Motivo (opcional)')}</p>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="reset-reason" />
        </div>
      </ConfirmDialog>

      {/* Capture confirm */}
      <ConfirmDialog
        open={captureOpen} onOpenChange={setCaptureOpen} busy={busy}
        title="Capture a new golden version"
        description="This changes the official demonstration baseline. The capture is immutable and audited."
        phrase="CAPTURE GOLDEN" onConfirm={doCapture} testid="capture-dialog"
      >
        <div className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">{L('Short description (required)','Descripción corta (obligatoria)')}</p>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} data-testid="capture-description" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">{L('Notes (optional)','Notas (opcional)')}</p>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} className="min-h-[70px]" data-testid="capture-notes" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={markCanonical} onChange={(e) => setMarkCanonical(e.target.checked)} data-testid="capture-canonical-check" />
            Mark as the Canonical Demo State
          </label>
        </div>
      </ConfirmDialog>
    </>
  );
}
